import { useState } from 'react';
import { BellOff, BellRing, Download, Share, SquarePlus } from 'lucide-react';

import { usePushAlerts } from '@/hooks/use-push-alerts';
import { promptInstall, useCanInstall } from '@/lib/install';
import { cn } from '@/lib/utils';
import { Button } from './ui/button';
import { Card, CardBody } from './ui/card';

const SNOOZE_KEY = 'tlp.rider.alerts-snoozed-until';
const SNOOZE_MS = 24 * 60 * 60 * 1000;

function snoozed(): boolean {
  try {
    return Number(localStorage.getItem(SNOOZE_KEY) ?? 0) > Date.now();
  } catch {
    return false;
  }
}

/**
 * Turning on job alerts, explained in the rider's terms.
 *
 * Not "Enable push notifications" — that is a sentence about the browser.
 * This says what the rider gets: a buzz for every new job, with where to go
 * and how much cash, even with the app closed. Every path to "on" is spelled
 * out, including the two that cannot be done from a button: an iPhone has to
 * add the app to the home screen first, and a refused permission can only be
 * lifted in the browser's own settings.
 *
 * `compact` is the account screen's version: a status line that is always
 * there, rather than a prompt that goes away.
 */
export function AlertsCard({ compact = false, className }: { compact?: boolean; className?: string }) {
  const { state, working, error, enable } = usePushAlerts();
  const canInstall = useCanInstall();
  const [hidden, setHidden] = useState(snoozed);

  if (state === null) return null;

  if (compact) {
    return <AlertsStatus state={state} working={working} error={error} onEnable={enable} className={className} />;
  }

  // The run sheet only asks; it does not report. "On" needs no card, and a
  // phone that cannot do it is told once, on the account screen.
  if (hidden || state === 'enabled' || state === 'unsupported' || state === 'unavailable') {
    return null;
  }

  function snooze() {
    try {
      localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
    } catch {
      /* hidden for this visit only */
    }
    setHidden(true);
  }

  if (state === 'needs-install') {
    return (
      <Card variant="wash" className={className}>
        <CardBody className="grid gap-3">
          <Heading icon={BellRing} title="Get a buzz for every new job" />
          <p className="text-sm leading-snug text-ink-muted">
            On an iPhone, job alerts only work once this app is on your home screen. It takes
            three taps:
          </p>
          <ol className="grid gap-2 text-sm leading-snug text-ink">
            <Step n={1}>
              Tap <Share className="inline size-4 align-[-2px]" aria-label="Share" /> Share at the
              bottom of Safari
            </Step>
            <Step n={2}>
              Choose <SquarePlus className="inline size-4 align-[-2px]" aria-hidden="true" />{' '}
              <strong>Add to Home Screen</strong>
            </Step>
            <Step n={3}>Open the app from your home screen and turn alerts on</Step>
          </ol>
          <Button variant="ghost" size="sm" className="justify-self-start" onClick={snooze}>
            Not now
          </Button>
        </CardBody>
      </Card>
    );
  }

  if (state === 'denied') {
    return (
      <Card variant="warning" className={className}>
        <CardBody className="grid gap-2">
          <Heading icon={BellOff} title="Job alerts are blocked" tone="warning" />
          <p className="text-sm leading-snug text-ink">
            New jobs will only show while this app is open. To get a buzz instead, tap the lock
            next to the web address, open <strong>Notifications</strong> and choose{' '}
            <strong>Allow</strong>.
          </p>
          <Button variant="ghost" size="sm" className="justify-self-start" onClick={snooze}>
            Not now
          </Button>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card variant="wash" className={className}>
      <CardBody className="grid gap-3">
        <Heading icon={BellRing} title="Get a buzz for every new job" />
        <p className="text-sm leading-snug text-ink-muted">
          Your phone will tell you which shop to go to, how far the trip is and any cash to
          collect — even when this app is closed.
        </p>
        {error ? (
          <p role="alert" className="text-sm font-medium text-error-ink">
            {error}
          </p>
        ) : null}
        <div className="flex flex-wrap items-start gap-2">
          <Button size="lg" loading={working} loadingLabel="Turning on" onClick={() => void enable()}>
            <BellRing aria-hidden="true" />
            Turn on job alerts
          </Button>
          {canInstall ? (
            <Button variant="secondary" size="lg" onClick={() => void promptInstall()}>
              <Download aria-hidden="true" />
              Add to home screen
            </Button>
          ) : null}
        </div>
        <Button variant="ghost" size="sm" className="justify-self-start" onClick={snooze}>
          Not now
        </Button>
      </CardBody>
    </Card>
  );
}

function Heading({
  icon: Icon,
  title,
  tone = 'brand',
}: {
  icon: typeof BellRing;
  title: string;
  tone?: 'brand' | 'warning';
}) {
  return (
    <p
      className={cn(
        'flex items-center gap-2 text-base font-bold leading-snug',
        tone === 'brand' ? 'text-brand' : 'text-warning-ink',
      )}
    >
      <Icon className="size-5 shrink-0" aria-hidden="true" />
      {title}
    </p>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <span
        data-numeric
        aria-hidden="true"
        className="grid size-6 shrink-0 place-items-center rounded-full bg-primary text-xs font-bold text-on-primary"
      >
        {n}
      </span>
      <span className="min-w-0 flex-1 pt-0.5">{children}</span>
    </li>
  );
}

/** The account screen's line: always present, says where things stand. */
function AlertsStatus({
  state,
  working,
  error,
  onEnable,
  className,
}: {
  state: NonNullable<ReturnType<typeof usePushAlerts>['state']>;
  working: boolean;
  error: string | null;
  onEnable: () => Promise<void>;
  className?: string;
}) {
  const copy: Record<typeof state, string> = {
    enabled: 'On. This phone buzzes when you get a new job.',
    default: 'Off. Turn them on to hear about new jobs with the app closed.',
    denied: 'Blocked in your browser. Allow Notifications for this site to turn them on.',
    'needs-install': 'Add this app to your home screen first, then turn them on here.',
    unavailable: 'Not switched on for the platform yet. New jobs still show in the app.',
    unsupported: 'This browser cannot show alerts. New jobs still show in the app.',
  };

  return (
    <Card className={className}>
      <CardBody className="grid gap-3">
        <div className="flex items-start gap-3">
          <span
            className={cn(
              'grid size-10 shrink-0 place-items-center rounded-lg',
              state === 'enabled' ? 'bg-wash text-brand' : 'bg-muted text-ink-subtle',
            )}
          >
            {state === 'enabled' ? (
              <BellRing className="size-5" aria-hidden="true" />
            ) : (
              <BellOff className="size-5" aria-hidden="true" />
            )}
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-ink">Job alerts</p>
            <p className="mt-0.5 text-sm leading-snug text-ink-muted">{copy[state]}</p>
          </div>
        </div>
        {error ? (
          <p role="alert" className="text-sm font-medium text-error-ink">
            {error}
          </p>
        ) : null}
        {state === 'default' ? (
          <Button
            size="md"
            className="justify-self-start"
            loading={working}
            loadingLabel="Turning on"
            onClick={() => void onEnable()}
          >
            Turn on job alerts
          </Button>
        ) : null}
      </CardBody>
    </Card>
  );
}
